/**
 * GTD Service Factory - Simplified Integration
 * 
 * Following Party Manager ServiceFactory pattern - simple, type-safe, 
 * no dependency injection complexity. This eliminates all the Inversify
 * symbol binding issues and makes the GTD domain much more stable.
 * 
 * Inspired by Party Manager's success with this approach.
 */

import { Db } from 'mongodb';
import { createGTDQueryService, GTDQueryService, PersonLookupService } from '../../bounded-contexts/inbox-management/infrastructure/factories/createGTDQueryService';

/**
 * Simplified GTD Service Factory
 * No Inversify, no symbols, just simple factory pattern like Party Manager
 */
export class GTDServiceFactory {
  private static instance: GTDServiceFactory;
  
  private database?: Db;
  private initialized = false;
  
  // Service instances (lazy loaded)
  private queryService?: GTDQueryService;

  private constructor() {
    // Simple constructor - no complex DI setup
  }

  static getInstance(): GTDServiceFactory {
    if (!GTDServiceFactory.instance) {
      GTDServiceFactory.instance = new GTDServiceFactory();
    }
    return GTDServiceFactory.instance;
  }

  /**
   * Initialize with database connection
   * Should be called once at application startup
   */
  async initialize(database: Db): Promise<void> {
    try {
      this.database = database;
      this.initialized = true;
      console.log('✅ GTD Service Factory initialized successfully');
    } catch (error: any) {
      console.error('❌ Failed to initialize GTD Service Factory:', error);
      throw new Error(`GTD Service Factory initialization failed: ${error.message}`);
    }
  }

  /**
   * Check if factory is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get database instance
   */
  private getDatabase(): Db {
    if (!this.database) {
      throw new Error('GTD Service Factory not initialized. Call initialize() first.');
    }
    return this.database;
  }

  // ========================================================================
  // Service Getters (Lazy Loading) - Party Manager Pattern
  // ========================================================================

  /**
   * Get GTD Query Service
   * Creates the service using the existing factory function
   */
  getQueryService(personLookupService?: PersonLookupService): GTDQueryService {
    if (!this.queryService) {
      // Use existing factory - no need to reinvent
      this.queryService = createGTDQueryService(undefined, personLookupService);
    }
    return this.queryService;
  }

  /**
   * Get health status
   */
  async getHealthStatus(): Promise<any> {
    const status = {
      healthy: false,
      database: 'disconnected',
      initialized: this.initialized,
      timestamp: new Date().toISOString()
    };

    try {
      if (this.database) {
        // Simple ping test
        await this.database.admin().ping();
        status.database = 'connected';
        status.healthy = true;
      }
    } catch (error: any) {
      console.error('GTD Service Factory health check failed:', error);
    }

    return status;
  }

  /**
   * Graceful cleanup
   */
  async cleanup(): Promise<void> {
    if (this.queryService) {
      this.queryService = undefined;
    }
    this.database = undefined;
    this.initialized = false;
    console.log('GTD Service Factory cleanup completed');
  }
}